<template>
<md-app class="app" md-mode="fixed">

  <nav-bar slot="md-app-toolbar" type="filter"></nav-bar>

  <md-app-drawer id="sidebar" :md-permanent.sync="$store.state.sidebarType" :md-active.sync="$store.state.sidebarActive">
    <md-app id="sidebar-content" md-mode="fixed" :md-scrollbar="false">

      <md-app-toolbar id="filter-toolbar" md-dense md-elevation="0">
        <md-autocomplete
          ref="lf-filter-search"
          v-model="filterSearchResult"
          :md-options="filterSearchResults"
          @md-changed="getFilterSearchResults"
          @md-opened="getFilterSearchResults"
          @md-selected="setFilterSearchResult"
          :md-input-placeholder="$t('keyword_search')"
          :md-fuzzy-search="false"
          md-dense>
          <template slot="md-autocomplete-item" slot-scope="{ item, term }">
            <span>
              <span class="lf-autocomplete-label"><b>{{ item.label }}:</b></span>
              <span v-if="item.translatedParentValue" class="lf-autocomplete-parent-label">
                <span v-if="item.parentMatch && term && typeof term === 'string'" style="display:inline-block">
                  <md-highlight-text
                    :md-term="term"
                    :md-fuzzy-search="false">
                    {{ item.translatedParentValue }}
                  </md-highlight-text>
                </span>
                <span v-else>
                  {{ item.translatedParentValue }}
                </span>
                <md-icon>keyboard_arrow_right</md-icon>
              </span>
              <span v-if="item.valueMatch && term && typeof term === 'string'" class="lf-autocomplete-value-highlight">
                <md-highlight-text
                  :md-term="term"
                  :md-fuzzy-search="false">
                  {{ item.translatedValue }}
                </md-highlight-text>
              </span>
              <span v-else class="lf-autocomplete-value">
                {{ item.translatedValue }}
              </span>
            </span>
            <md-icon v-if="item.active">check_circle_outline</md-icon>
          </template>
          <template slot="md-autocomplete-empty" slot-scope="{ term }">
            <span v-if="term">{{ $t('no_matching_filters_found') }}</span>
          </template>
        </md-autocomplete>
      </md-app-toolbar>

      <md-app-content>
        <md-table class="lf-filter-table" v-model="$store.state.filters" md-sort="group" md-sort-order="asc">
          <md-table-row slot="md-table-row" slot-scope="{ item }">
            <md-table-cell md-label="Filter" md-sort-by="label">

              <div
                v-if="Object.keys(item.options).length > 3 || item.type === 'hierarchical'"
                @click="showFilterDialogFor(item)"
                class="md-layout lf-filter">
                <div class="md-layout-item" :class="{ muted: valueUnset(item) }">
                  <div v-if="$te(`filters.label.${item.label}`)" class="md-body-2 mt-1">
                    {{ $t(`filters.label.${item.label}`) }}
                  </div>
                  <div v-else class="md-body-2 mt-1">
                    Filter
                  </div>
                  <div class="mb-1" style="min-height: 24px;">
                    <span v-if="valueUnset(item)"
                      class="md-body-2 text-smaller text-italic">
                      {{ $t('all') }}
                    </span>
                    <span v-else class="md-body-1 text-smaller">
                      <div>{{ getLabel(item) }}</div>
                    </span>
                  </div>
                </div>
              </div>

              <div v-else class="md-layout lf-filter">
                <div class="md-layout-item" :class="{muted: valueUnset(item)}">
                  <div v-if="$te(`filters.label.${item.label}`)" class="md-body-2 mt-1">
                    {{ $t(`filters.label.${item.label}`) }}
                  </div>
                  <div v-else class="md-body-2 mt-1">
                    Filter
                  </div>
                  <div class="mt-1 mb-1" style="min-height: 24px;">
                    <div v-if="item.type === 'categorical'">
                      <div v-for="(optName, index) in item.options" :key="index">
                        <div v-if="item.multiple">
                          <md-checkbox
                            v-model="$store.state.filterValues.filters[item.name].values"
                            :value="optName"
                            @change="getResults"
                            class="md-primary mt-0 mb-2">
                            <span v-if="$te(`filters.value.${item.label}['${index}']`)">
                              {{ $t(`filters.value.${item.label}['${index}']`) }}
                            </span>
                            <span v-else>
                              {{ `${optName.charAt(0).toUpperCase()}${optName.slice(1)}` }}
                            </span>
                          </md-checkbox>
                        </div>
                        <div v-else>
                          <md-radio
                            v-model="$store.state.filterValues.filters[item.name].values"
                            :value="optName"
                            @change="getResults"
                            class="md-primary mt-0 mb-2">
                            <span v-if="$te(`filters.value.${item.label}['${index}']`)">
                              {{ $t(`filters.value.${item.label}['${index}']`) }}
                            </span>
                            <span v-else>
                              {{ `${optName.charAt(0).toUpperCase()}${optName.slice(1)}` }}
                            </span>
                          </md-radio>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </md-table-cell>
          </md-table-row>
        </md-table>
      </md-app-content>

    </md-app>
  </md-app-drawer>

  <md-app-content>
    <md-app id="content" md-mode="fixed">

      <md-app-toolbar id="search-toolbar" md-dense md-elevation="0">
        <div class="md-layout md-alignment-center-space-between">
          <div class="md-layout-item md-size-45 md-small-size-40 md-xsmall-size-60" :style="{ 'padding-right': ($store.state.screenSize === 'xsmall') ? '2.5%' : '1.5%' }">
            <md-field>
              <md-input v-model="$store.state.filterValues.keywords" :placeholder="$t('full_text_search')" @keyup.enter="getResults"></md-input>
            </md-field>
          </div>
          <div class="md-layout-item md-size-25 md-small-size-25 md-xsmall-size-40" :style="{ 'padding-right': ($store.state.screenSize === 'xsmall') ? 0 : '1.5%' }">
            <md-field>
              <label for="sort">{{ $t('sort') }}</label>
              <md-select
                v-model="$store.state.filterValues.sort"
                @md-selected="sortChanged"
                id="sort"
                md-dense>
                <md-option value="newest">{{ $t('newest') }}</md-option>
                <md-option value="oldest">{{ $t('oldest') }}</md-option>
                <md-option value="relevance">{{ $t('relevance') }}</md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item md-size-30 md-small-size-35 md-xsmall-size-100">
            <div v-if="$store.state.screenSize !== 'xsmall'">
              <div style="display:inline-block;width:55%;margin-right:5%">
                <md-button class="md-raised" @click="unsetResults">
                  {{ $t('reset') }}
                </md-button>
              </div>
              <div style="display:inline-block;width:40%;float:right">
                <md-button class="md-primary md-raised" @click="getResults">
                  {{ $t('search') }}
                </md-button>
              </div>
            </div>
            <div v-else>
              <div style="width:100%">
                <md-button class="md-raised" @click="unsetResults">
                  {{ $t('reset') }}
                </md-button>
              </div>
              <div style="width:100%">
                <md-button class="md-primary md-raised" @click="getResults">
                  {{ $t('search') }}
                </md-button>
              </div>
            </div>
          </div>
        </div>
      </md-app-toolbar>

      <md-app-content id="search-results" style="height:100%">
        <div v-if="!$store.state.loading && results.length === 0" class="md-layout md-alignment-center-center" style="height:100%">
          <div style="text-align:center">
            <md-icon class="md-size-2x mb-1">error_outline</md-icon>
            <div>{{ $t('no_results_found') }}</div>
          </div>
        </div>
        <div v-else class="content-container mb-4">
          <md-list>
            <div v-for="item in results" :key="item._id">
              <div class="md-layout mt-2 mb-2">

                <div class="md-layout-item md-size-33 md-xsmall-size-100" :style="{ 'padding-right': ($store.state.screenSize === 'xsmall') ? 0 : '2%' }">
                  <div class="lf-thumbnail-wrapper" @click="showImageDialogFor(item)">
                    <img class="lf-thumbnail" :ref="`thumbnail-${item._id}`" src="../assets/image-default.jpg" />
                    <md-icon class="lf-thumbnail-btn md-size-2x">zoom_in</md-icon>
                  </div>
                </div>

                <div class="md-layout-item md-size-66 md-xsmall-size-100">
                  <md-list-item>
                    <div class="md-list-item-text">
                      <span v-if="item.report_title" class="lf-item-title" @click="navigateTo(item.report_id)">{{ item.report_title }}</span>
                      <span v-if="item.slideshow_title" class="mt-1" style="white-space:normal">{{ item.slideshow_title }}</span>
                      <span v-if="item.title" class="mt-1" style="white-space:normal">{{ item.title }}</span>
                      <span v-if="item.action_title" class="mt-1" style="white-space:normal"><b>{{ item.action_title }}</b></span>
                      <!--span v-if="item.date" style="white-space:normal"><b>Datum:</b> {{ item.date }}</span-->
                      <span class="mt-3">
                        <div class="pt-1 pb-1">
                          <div class="lf-button-report">
                            <md-button
                              class="md-raised"
                              @click="navigateTo(item.report_id)">
                              <span v-if="!!$store.state.user.client.report_button_text">
                                {{ $t('go_to_custom_report', [$store.state.user.client.report_button_text]) }}
                              </span>
                              <span v-else>
                                {{ $t('go_to_report') }}
                              </span>
                            </md-button>
                          </div>
                          <div v-for="(link, index) in item.links" :key="`link-${index}`" class="lf-button-report">
                            <md-button
                              class="md-raised"
                              @click="openUrl(link.target)">
                              <span v-if="!!link.description">
                                {{ link.description }}
                              </span>
                              <span v-else>
                                Link {{ index + 1 }}
                              </span>
                            </md-button>
                          </div>
                        </div>
                      </span>
                    </div>
                  </md-list-item>
                </div>

              </div>
              <md-divider />
            </div>
            <div v-if="endOfResults" class="mt-3" style="text-align:center">
              <md-icon class="md-size-2x mb-1">arrow_upward</md-icon>
              <div>{{ $t('end_of_results') }}</div>
            </div>
          </md-list>
        </div>

        <dialog-modal
          type="filter"
          :filter.sync="filter"
          :showFilterDialog.sync="showFilterDialog"
          v-on:getResults="getResults"
        />

        <dialog-modal
          type="image"
          :imageData.sync="imageData"
          :showImageDialog.sync="showImageDialog"
        />
      </md-app-content>
    </md-app>
  </md-app-content>
</md-app>
</template>

<script>
import _ from 'lodash'
import i18n from '@/i18n'

export default {
  name: 'Start',
  data: () => ({
    showFilterDialog: false,
    showImageDialog: false,
    filter: {},
    imageData: {},
    loadingMore: false,
    viewFilterChanged: false,
    initResults: true,
    results: [],
    endOfResults: false,
    resultsPage: 0,
    contentScrollerEl: null,
    filterSearchEl: null,
    filterSearchMenuEl: null,
    filterSearchResult: '',
    filterSearchResults: []
  }),
  mounted () {
    const $vm = this
    // Set content scroller element
    $vm.contentScrollerEl = document.querySelector('#content').querySelector('.md-app-scroller')
    // Set tabindex to make keydown event listener work
    $vm.contentScrollerEl.tabIndex = 0
    // Add scroll event listeners
    $vm.contentScrollerEl.addEventListener('keydown', $vm.loadMoreOnArrowDown)
    _.each(['wheel', 'mousewheel', 'DOMMouseScroll', 'touchmove'], event => {
      $vm.contentScrollerEl.addEventListener(event, $vm.loadMore)
    })

    // Set filter search elements
    $vm.filterSearchEl = $vm.$refs['lf-filter-search']
    $vm.filterSearchMenuEl = $vm.getChildByClassName($vm.filterSearchEl, 'md-menu-content')
    // Add filter search navigation event
    $vm.filterSearchEl.$el.querySelector('.md-input').addEventListener('keydown', $vm.navigateThroughMenuItems)

    // Set initial results and filters
    $vm.$store.dispatch('setLoading', true)
    $vm.$store.dispatch('getTranslations')
    $vm.$store.dispatch('getFilters')
      .then(() => {
        let urlFilterValues
        // Check for url filters
        if (localStorage.hasOwnProperty('urlFilter')) {
          urlFilterValues = localStorage.getItem('urlFilter')
          localStorage.removeItem('urlFilter')
        } else {
          window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => { if (key === 'filter') urlFilterValues = value })
        }
        if (urlFilterValues) {
          // If url congains filter params, get results based on these filters
          $vm.getResults(null, false, urlFilterValues)
          // Remove filter from url
          history.pushState({}, null, window.location.href.split('?')[0])
        } else {
          $vm.getResults(null, true)
        }
      })
  },
  beforeDestroy () {
    const $vm = this
    // Remove content scroll event listeners
    $vm.contentScrollerEl.removeEventListener('keydown', $vm.loadMoreOnArrowDown)
    _.each(['wheel', 'mousewheel', 'DOMMouseScroll', 'touchmove'], event => {
      $vm.contentScrollerEl.removeEventListener(event, $vm.loadMore)
    })
    // Remove filter search navigation event
    $vm.filterSearchEl.$el.querySelector('.md-input').removeEventListener('keydown', $vm.navigateThroughMenuItems)
  },
  methods: {
    showFilterDialogFor (filter) {
      this.filter = filter
      this.showFilterDialog = true
    },
    showImageDialogFor (item) {
      const data = {}
      data.image = item.image
      data.media = item.media
      this.imageData = data
      this.showImageDialog = true
    },
    getLabel (item) {
      const $vm = this
      let values = $vm.$store.state.filterValues.filters[item.name].values
      // Display Labels for categorical filters
      if (_.isArray(values)) {
        const labels = []

        _.each(values, value => {
          const key = (_.invert(item.options))[value]
          // Check if translation exists
          if ($vm.$i18n.te(`filters.value.${item.label}.${key}`)) {
            labels.push($vm.$i18n.t(`filters.value.${item.label}.${key}`))
          // Display value as fallback
          } else {
            labels.push(value)
          }
        })
        return labels.join(', ')
      // Display Labels for hierarchical filters
      } else if (_.isObject(values)) {
        const labelArr = []

        _.each(item.options, option => {
          // Check if values were added
          if (values[option.name] && values[option.name].length !== 0) {
            const labels = []
            let label

            // Check if translation for parent label exists
            if ($vm.$i18n.te(`filters.value.${item.label}['${option.label}']`)) {
              label = $vm.$i18n.t(`filters.value.${item.label}['${option.label}']`)
            } else {
              label = option.label
            }
            // Iterate through added child values
            _.each(values[option.name], value => {
              // Check if translation for child label exists
              if ($vm.$i18n.te(`filters.value.${item.label}['${value}']`)) {
                labels.push($vm.$i18n.t(`filters.value.${item.label}['${value}']`))
              } else {
                labels.push(value)
              }
            })

            labelArr.push(`${label}: ${labels.join(', ')}`)
          }
        })
        return labelArr.join(' | ')
      } else if (_.isString(values)) {
        let label
        const key = (_.invert(item.options))[values]
        // Check if translation for parent label exists
        if ($vm.$i18n.te(`filters.value.${item.label}.${key}`)) {
          label = $vm.$i18n.t(`filters.value.${item.label}.${key}`)
        // Display value as fallback
        } else {
          label = values
        }
        return label
      }
    },
    valueUnset (item) {
      let val = this.$store.state.filterValues.filters[item.name].values
      if (_.isArray(val)) {
        return val.length === 0
      } else if (_.isObject(val)) {
        let numSelected = 0
        _.each(val, values => {
          if (values.length !== 0) numSelected++
        })
        return numSelected === 0
      } else if (_.isString(val)) {
        return val === ''
      }
    },
    getResults (val = null, init = false, urlFilterValues = null) {
      const $vm = this
      $vm.initResults = init // Indicates if user already selected filters or not

      if (!init) {
        // Check if view filter exists
        const viewFilter = $vm.$store.state.filters.find(filter => filter.name === 'view')
        if (viewFilter && viewFilter.options && $vm.$store.state.filterValues.filters.view) {
          // set "detail charts" as view filter value
          const { covercharts, detailcharts } = viewFilter.options
          if (val === covercharts) $vm.viewFilterChanged = true
          if (!$vm.viewFilterChanged) $vm.$store.state.filterValues.filters.view.values = detailcharts
        }
      }

      $vm.$store.dispatch('getResults', { page: 0, urlFilterValues })
        .then(data => {
          $vm.resultsPage = 0 // Reset page number since we're loading new results
          $vm.endOfResults = data.endOfResults
          $vm.results = data.results
          $vm.contentScrollerEl.scrollTop = 0 // Scroll back to top of content scroller
          $vm.setThumbs(data.results)
          $vm.$store.dispatch('setLoading', false)
        })
        .catch(err => console.error(err))
    },
    unsetResults () {
      const $vm = this
      $vm.$store.dispatch('setEmptyFilterValues')
      $vm.$store.state.filterValues.keywords = ''
      $vm.$store.state.filterValues.sort = ''
      $vm.filterSearchResult = ''
      $vm.viewFilterChanged = false
      $vm.getResults(null, true)
    },
    sortChanged (sort) {
      if (sort) this.getResults()
    },
    getFilterSearchResults (term) {
      const $vm = this
      $vm.filterSearchResults = new Promise(resolve => {
        // When the user selects an item, the change event of the input field is fired (just as when the user types)
        // If the search term is an object, it means that the user just initiated a search
        // If so, we clear the results
        if (term && _.isObject(term)) $vm.filterSearchResult = ''

        // Reset highlight index
        $vm.filterSearchMenuEl.highlightIndex = -1

        // Get search results
        window.setTimeout(() => {
          let searchTerm = null
          const searchResults = []

          if (term && typeof term === 'string') searchTerm = term.toLowerCase()

          const addSearchResult = (filter, value, name, parentValue, parentName, children) => {
            const { label, type, multiple } = filter
            const translatedValue = (i18n.te(`filters.value.${label}['${name}']`)) ? i18n.t(`filters.value.${label}['${name}']`) : `${value.charAt(0).toUpperCase()}${value.slice(1)}`
            const valueMatch = translatedValue.toLowerCase().includes(searchTerm)
            let translatedParentValue = null
            let parentMatch = false
            if (parentValue && parentName) {
              translatedParentValue = (i18n.te(`filters.value.${label}['${parentName}']`)) ? i18n.t(`filters.value.${label}['${parentName}']`) : `${parentValue.charAt(0).toUpperCase()}${parentValue.slice(1)}`
              parentMatch = translatedParentValue.toLowerCase().includes(searchTerm)
            }
            // Output all values if no search term is given
            // Otherwise, check if value contains search term
            if (!searchTerm || valueMatch || parentMatch) {
              const optionStr = (translatedParentValue) ? `${translatedParentValue} - ${translatedValue}` : translatedValue

              const item = {
                label: (i18n.te(`filters.label.${label}`)) ? i18n.t(`filters.label.${label}`) : 'Filter',
                value,
                valueMatch,
                translatedValue,
                name,
                translatedParentValue,
                parentName,
                parentMatch,
                children,
                filterName: filter.name,
                type,
                multiple,
                toLowerCase: () => optionStr.toLowerCase(),
                toString: () => optionStr
              }

              // Check if filter was already applied
              if ($vm.isFilterAlreadyApplied(item)) item.active = true

              // Add option to search results
              searchResults.push(item)
            }
          }

          // Iterate through all filters
          _.each($vm.$store.state.filters, filter => {
            const { options, type } = filter
            // Process categorical filters
            if (_.isObject(options) && type === 'categorical') {
              _.each(options, (value, name) => {
                addSearchResult(filter, value, name)
              })
            // Process hierarchical filters
            } else if (_.isArray(options) && type === 'hierarchical') {
              _.each(options, option => {
                addSearchResult(filter, option.value, option.name, null, null, option.children)
                if (option.children) {
                  _.each(option.children, child => {
                    addSearchResult(filter, child.value, child.name, option.value, option.name)
                  })
                }
              })
            }
          })

          // If the search term is an object, it means that the user just initiated a search
          // If so, we focus the input field and show the menu dropdown
          if (term && _.isObject(term)) {
            $vm.filterSearchEl.$el.querySelector('.md-input').focus() // Focus input field
            $vm.filterSearchEl.showMenu = true // Show the dropdown menu
          }

          resolve(searchResults)
        }, 100)
      })
    },
    setFilterSearchResult (item) {
      const $vm = this
      const { value, name, parentName, children, filterName, type, multiple } = item
      let refresh = false

      // Add filter value, if not applied yet
      switch (type) {
        case 'categorical':
          if (!$vm.isFilterAlreadyApplied(item)) {
            // Check if multiple values can be set or not
            if (multiple) {
              $vm.$store.state.filterValues.filters[filterName].values.push(value)
            } else {
              $vm.$store.state.filterValues.filters[filterName].values = value
            }
            refresh = true
          }
          break
        case 'hierarchical':
          if (!$vm.isFilterAlreadyApplied(item)) {
            if (parentName) { // Process child values
              $vm.$store.state.filterValues.filters[filterName].values[parentName].push(value)
            } else if (children) { // Process parent values
              const childrenValues = _.map(children, 'value')
              $vm.$store.state.filterValues.filters[filterName].values[name] = _.union($vm.$store.state.filterValues.filters[filterName].values[name], childrenValues)
            }
            refresh = true
          }
          break
      }
      // Refresh search results if neccessary
      if (refresh) $vm.getResults(value)
    },
    isFilterAlreadyApplied (item) {
      const $vm = this
      const { value, name, parentName, children, filterName, type } = item
      let filterValues = $vm.$store.state.filterValues.filters[filterName].values
      switch (type) {
        case 'categorical':
          return filterValues.includes(value)
        case 'hierarchical':
          if (parentName) { // Process child values
            return filterValues[parentName].includes(value)
          } else if (children) { // Process parent values
            // Check if all child values have been already applied
            return filterValues[name].length === children.length
          }
          return false
      }
    },
    navigateThroughMenuItems ({keyCode}) {
      const $vm = this
      // Check if arrow down or arrow up button was pressed
      if (keyCode === 40 || keyCode === 38) {
        const menuItem = $vm.filterSearchMenuEl.$el.querySelector('.md-highlight')
        if (menuItem) {
          const { offsetTop, offsetHeight } = menuItem
          // Jump to current menu item
          if (keyCode) $vm.filterSearchMenuEl.$el.querySelector('.md-menu-content-container').scrollTop = (keyCode === 40) ? offsetTop + offsetHeight : offsetTop - offsetHeight
          else $vm.filterSearchMenuEl.$el.querySelector('.md-menu-content-container').scrollTop = offsetTop + offsetHeight
        }
      }
    },
    loadMoreOnArrowDown ({keyCode}) {
      if (keyCode === 40) this.loadMore()
    },
    loadMore () {
      const $vm = this
      const scrollPos = $vm.contentScrollerEl.scrollHeight - $vm.contentScrollerEl.scrollTop
      const loadMorePos = $vm.contentScrollerEl.clientHeight + 100
      // If user reaches bottom of scroller element
      if (!$vm.endOfResults && !$vm.loadingMore && scrollPos <= loadMorePos) {
        $vm.loadingMore = true
        $vm.resultsPage++ // Increase page number
        $vm.$store.dispatch('getResults', { page: $vm.resultsPage, init: $vm.initResults }) // Get results of next page
          .then(data => {
            $vm.endOfResults = data.endOfResults
            $vm.results = $vm.results.concat(data.results)
            $vm.setThumbs(data.results)
            $vm.$store.dispatch('setLoading', false)
            setTimeout(() => { $vm.loadingMore = false }, 1000)
          })
          .catch(err => console.error(err))
      }
    },
    setThumbs (results) {
      const $vm = this
      const urls = results.map(item => item.thumb)
      $vm.$store.dispatch('getFiles', urls)
        .then(files => {
          _.each(files, (file, index) => {
            const $el = $vm.$refs[`thumbnail-${results[index]._id}`][0] // Get element that displays relevant thumbnail
            $el.src = file // Set thumbnail as image source
          })
        })
        .catch(err => console.error(err))
    },
    navigateTo (id) {
      this.$router.push({ name: 'report', params: { id } })
    },
    openUrl (url) {
      if (url && url !== '') window.open(url)
    },
    getChildByClassName (component, className) {
      // Check if component has children
      if (component.$children.length !== 0) {
        let match
        const iterateThroughChildren = children => {
          _.each(children, child => {
            // Check if child with given class name exists
            if (child.$options._componentTag === className) {
              match = child
              return null // End loop
            // If child has children, recursively check for matching elements
            } else if (child.$children.length !== 0) {
              iterateThroughChildren(child.$children)
            }
          })
        }
        iterateThroughChildren(component.$children)
        return match
      }
      return null
    }
  }
}
</script>

<style lang="scss">
  @import "../styles/theme";

  .md-autocomplete-items {
    .lf-autocomplete-label {
      margin-right: 5px;
    }
    .lf-autocomplete-parent-label {
      .md-icon {
        margin-bottom: 3px;
      }
    }
    .lf-autocomplete-value-highlight {
      display: inline-block;
    }
    .md-list-item-content {
      padding-top: 0;
      padding-bottom: 0;
      min-height: 35px !important;
    }
    @media screen and (max-width: $xsmall) {
      .lf-autocomplete-label,
      .lf-autocomplete-parent-label,
      .lf-autocomplete-value,
      .lf-autocomplete-value-highlight {
        display: block;
      }
      .lf-autocomplete-label {
        margin-bottom: 4px;
      }
      .lf-autocomplete-parent-label {
        margin-top: -4px;
      }
      .md-list-item {
        border-bottom: 1px solid $border;
      }
      .md-list-item-content {
        padding-top: 7px;
        padding-bottom: 7px;
      }
    }
  }

  #search-toolbar {
    min-height:70px;
    background-color: $grey-light;
    border-bottom: 1px solid $border;
    .md-layout {
      width: calc(100% + 40px);
      margin: 0;
    }
    .md-button {
      // float: right;
      width: 100%;
      @media screen and (max-width: $xsmall) {
        // width: 100%;
      }
    }
  }

  #search-results {
    padding-top: 0;
  }

  #search-results {
    .md-list-item-content {
      padding-left: 0 !important;
      @media screen and (max-width: $xsmall) {
        padding: 0 !important;
        display: block !important;
      }
    }
  }

  #sidebar {
    .lf-filter-table thead {
      display: none;
    }
    .lf-filter-table tbody .md-table-row:first-child td {
      border: none;
    }
    .lf-filter {
      cursor: pointer;
      &:hover {
        color: $primary;
      }
    }
  }

  // Set Dropdown width
  .md-menu-content {
    width: auto !important;
    min-width: 242px !important;
    max-width: 1000px !important;
    max-height: 50vh !important;
  }

  .lf-thumbnail-wrapper {
    position: relative;
    transition: .5s ease;
    cursor: pointer;
    @media screen and (max-width: $xsmall) {
      margin-bottom: 20px;
    }
    &:hover {
      background-color: $primary;
      .lf-thumbnail {
        opacity: 0.25;
      }
      .lf-thumbnail-btn {
        opacity: 1;
      }
    }
  }
  .lf-thumbnail {
    width: 100%;
    // padding-top: 56.25%; /* 16:9 Aspect Ratio */
    // background-image: url('../assets/image-default.jpg');
    // background-repeat: no-repeat;
    // background-position: center;
    // background-size: cover;
    transition: .5s ease;
    backface-visibility: hidden;
  }
  .lf-thumbnail-btn {
    color: $grey-light !important;
    transition: .5s ease;
    opacity: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
  }

  .lf-item-title {
    cursor: pointer;
    font-weight: bold;
    white-space: normal;
    &:hover {
      color: $primary;
    }
  }

  .lf-button-report {
    float: left !important;
    padding: 2px !important;
    margin-right: 14px !important;
  }
  .lf-button-report:last-child {
    margin-right: 0 !important;
  }
  .lf-button-enlarge,
  .lf-button-report {
    width: auto !important;
  }
</style>
